import { createSelector, createSlice, current } from '@reduxjs/toolkit';
import uniq from 'lodash/uniq';

import {
  DeletePostAction,
  LoadEditPost,
  LoadMoreCommentsAction,
  LoadPostAction,
  PostsState,
  PublishAction,
  PushToListAction,
  ShareAction,
  RestorePostAction,
  SavePostAction,
  SetCommentsNextFromAction,
  SetListAction,
  SetPostAction,
  SetPostLoading,
  SetPostsAction,
  ToggleLikeAction,
  UnshiftToListAction,
  UpdateFormTextAction,
  ReplyCommentAction,
  LoadRepliesAction,
  SetRepliesLoadedAction,
  ToggleRepliesAction,
  SetSenderAction,
  LoadScheduledAction,
  SetScheduledAction,
  PublishScheduledAction,
  PinPostAction,
  UnpinPostAction,
  ReportPostAction,
  ReportCommentAction,
  HideAuthorAction,
  UpdateOrderList,
  UpdateFormTypeAction,
  ViewPostAction,
  ViewPostMultiAction,
  ClearFormAction,
  SendAdConversionAction,
  UpdateFormSendToTelegramAction,
  SetUnavailableAction,
  SetIsDeletedPostAction,
  SetTotalAction,
  IncTotalField,
} from './interface';
import { RootState } from 'store/rootReducer';
import { PostModel } from '$store/models';
import { useSelector } from 'react-redux';

const initialState: PostsState = {
  forms: {},
  posts: {},
  lists: {},
  total: { 1: 0, 2: 0, 3: 0 },
  isPostLoading: {},
  isCommentsLoadMore: {},
  commentsNextFrom: {},
  replies: {},
  postsUnavailabled: {},
  isDeletedPosts: {},

  isScheduledLoading: true,
  isScheduledFailed: false,
  scheduled: [],
  viewQueue: {},
};

function findPostsInAttachments(posts: PostModel[]) {
  const found: { [index: number]: PostModel } = {};

  for (const post of posts) {
    if (!post.attachments) {
      continue;
    }

    for (const item of post.attachments) {
      if (item.type === 'link' && item.link!.type === 'post') {
        found[item.link!.object.id] = item.link!.object;
      }
    }
  }

  return found;
}

export const { actions, reducer } = createSlice({
  name: 'posts',
  initialState,
  reducers: {
    updateFormText(state, action: UpdateFormTextAction) {
      const { formId, text } = action.payload;

      state.forms[formId] = {
        ...state.forms[formId],
        text,
      };
    },
    updateFormSendToTelegram(state, action: UpdateFormSendToTelegramAction) {
      const { formId, send } = action.payload;

      state.forms[formId] = {
        ...state.forms[formId],
        sendToTelegram: send,
      };
    },
    updateFormType(state, { payload: { formId, type } }: UpdateFormTypeAction) {
      state.forms[formId] = {
        ...(state.forms[formId] || {
          text: '',
        }),
        type,
      };
    },
    setSender(state, action: SetSenderAction) {
      const { formId, sender } = action.payload;

      state.forms[formId] = {
        ...(state.forms[formId] || {}),
        sender,
      };
    },

    publish(_, action: PublishAction) {},

    publishComment(_, action: PublishAction) {},

    setPosts(state, action: SetPostsAction) {
      const result = {
        ...findPostsInAttachments(action.payload),
      };
      for (let post of action.payload) {
        result[post.id] = post;
      }

      state.posts = {
        ...state.posts,
        ...result,
      };
    },

    setTotal(state, action: SetTotalAction) {
      state.total = action.payload;
    },

    incTotalFields(state, action: IncTotalField) {
      for (const key in action.payload) {
        if (state.total.hasOwnProperty(key)) {
          state.total[key] += action.payload[key];
        }
      }
    },

    setUnavailable(state, action: SetUnavailableAction) {
      state.postsUnavailabled[action.payload.id] = action.payload.isUnavailable;
    },

    setIsDeletedPost(state, action: SetIsDeletedPostAction) {
      state.isDeletedPosts[action.payload] = true;
    },

    setPost(state, action: SetPostAction) {
      const { id, updated } = action.payload;

      state.posts = {
        ...state.posts,
        [id]: {
          ...(state.posts[id] || {}),
          ...updated,
        },
        ...findPostsInAttachments([updated]),
      };
    },
    updateOrderList(state, action: UpdateOrderList) {
      let posts = state.lists[action.payload].map((postId) => state.posts[postId]);
      if (action.payload.includes('comments')) {
        // comments | sub comments
        const postId = String(action.payload).replace(/[^\d-]/g, '');
        const post = state.posts[postId];
        if (post.level > 0) {
          // sub comments
          posts.sort((a, b) => a.createdAt - b.createdAt);
        } else {
          // comments
          posts
            .sort((a, b) => b.createdAt - a.createdAt)
            .sort((a, b) => b.likes + b.comments * 2 - (a.likes + a.comments * 2));
        }
      } else {
        // posts
        posts.sort((a, b) => b.createdAt - a.createdAt);
      }

      state.lists[action.payload] = posts
        .sort((post) => (post.pinned ? -1 : 0))
        .map((post) => post.id);
    },

    setList(state, action: SetListAction) {
      state.lists[action.payload.listId] = action.payload.items;
    },

    unshiftToList(state, action: UnshiftToListAction) {
      state.lists[action.payload.listId] = uniq([
        ...action.payload.items,
        ...(state.lists[action.payload.listId] || []),
      ]);
    },

    pushToList(state, action: PushToListAction) {
      state.lists[action.payload.listId] = uniq([
        ...(state.lists[action.payload.listId] || []),
        ...action.payload.items,
      ]);
    },

    toggleLike(_, action: ToggleLikeAction) {},

    loadPost(state, action: LoadPostAction) {
      state.isPostLoading[action.payload] = true;
    },

    setPostLoading(state, action: SetPostLoading) {
      state.isPostLoading[action.payload.postId] = action.payload.isLoading;
    },

    deletePost(_, action: DeletePostAction) {},

    reportPost(_, action: ReportPostAction) {},

    reportComment(_, action: ReportCommentAction) {},

    restorePost(_, action: RestorePostAction) {},

    loadEditPost(_, action: LoadEditPost) {},

    savePost(_, action: SavePostAction) {},

    loadMoreComments(state, action: LoadMoreCommentsAction) {
      state.isCommentsLoadMore[action.payload] = true;
    },

    setCommentsNextFrom(state, action: SetCommentsNextFromAction) {
      const { postId, nextFrom } = action.payload;

      state.commentsNextFrom[postId] = nextFrom;
      delete state.isCommentsLoadMore[postId];
    },

    share(_, __: ShareAction) {},

    replyComment(state, action: ReplyCommentAction) {
      const { parentId, comment } = action.payload;

      const key = `comment_${parentId}`;
      state.forms[key] = {
        ...(state.forms[key] || {}),
        replyTo: comment,
      };
    },

    loadReplies(state, action: LoadRepliesAction) {
      const { postId } = action.payload;

      state.replies[postId] = {
        ...(state.replies[postId] || {}),
        isLoading: true,
        isShown: true,
      };
    },

    setRepliesLoaded(state, action: SetRepliesLoadedAction) {
      const { postId, nextFrom } = action.payload;

      state.replies[postId] = {
        ...(state.replies[postId] || {}),
        isLoading: false,
        nextFrom,
      };
    },

    toggleReplies(state, action: ToggleRepliesAction) {
      const { postId, isShown } = action.payload;

      state.replies[postId] = {
        ...(state.replies[postId] || {}),
        isShown,
      };
    },

    loadScheduled(state, action: LoadScheduledAction) {
      state.isScheduledLoading = true;
      state.isScheduledFailed = false;
      state.scheduled = [];
    },

    scheduledFailed(state) {
      state.isScheduledLoading = false;
      state.isScheduledFailed = true;
    },

    setScheduled(state, action: SetScheduledAction) {
      state.isScheduledLoading = false;
      state.isScheduledFailed = false;

      state.scheduled = action.payload
        .map((postId) => current(state.posts)[postId])
        .sort((a, b) => a.createdAt - b.createdAt)
        .map((post) => post.id);
    },

    viewPost(state, action: ViewPostAction) {
      const view = state.viewQueue[action.payload.postId];
      if (view) {
        state.viewQueue[action.payload.postId] = {
          ...view,
          ...action.payload,
        };
      } else {
        state.viewQueue[action.payload.postId] = action.payload;
      }
    },

    viewMultiPost(state, action: ViewPostMultiAction) {
      action.payload.forEach((view) => {
        state.viewQueue[view.postId] = view;
      });
    },

    sendSeenPosts() {},

    clearViewPostsList(state) {
      state.viewQueue = {};
    },

    publishScheduled(_, __: PublishScheduledAction) {},

    pinPost(_, __: PinPostAction) {},

    unpinPost(_, __: UnpinPostAction) {},
    hideAuthor(_, __: HideAuthorAction) {},
    clearForm(state, action: ClearFormAction) {
      state.forms[action.payload] = {
        text: '',
      };
    },
    sendAdConversion(_, __: SendAdConversionAction) {},
  },
});

export { reducer as postsReducer, actions as postsActions };

export const postsSelector = (state: RootState) => state.posts;
export const postsScheduledSelector = (state: RootState) => state.posts.scheduled;

export const createPostFormSelector = createSelector(
  postsSelector,
  (_, formId: string) => formId,
  (posts, formId) => {
    return posts.forms[formId] || { text: '' };
  },
);
export const postsListSelector = createSelector(
  postsSelector,
  (_, params: { listId: string }) => params,
  (questions, { listId }) => {
    return questions.lists[listId] || [];
  },
);

export const postSelector = createSelector(
  postsSelector,
  (_, postId: number) => postId,
  (questions, postId) => {
    return questions.posts[postId];
  },
);

export const totalSelector = createSelector(postsSelector, (state) => {
  return state.total;
});

export const usePostIsUnavailableSelector = (postId: number): boolean => {
  const { postsUnavailabled } = useSelector((state: RootState) => postsSelector(state));
  return postsUnavailabled[postId];
};

export const usePostIsDeletedSelector = (postId: number): boolean => {
  const { isDeletedPosts } = useSelector((state: RootState) => postsSelector(state));
  return isDeletedPosts[postId];
};
